// DEPRECATED: Prefer using the useVendors() hook from app/src/hooks/useVendors.ts directly.
// This context is kept for backward compatibility.

import React, { createContext, useContext, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { vendorService } from "@/services/vendorService";

export interface Vendor {
  id: string;
  name: string;
  category: "platform" | "consulting" | "data" | "infrastructure" | "other";
  status: "active" | "evaluating" | "inactive";
  website: string;
  contactEmail: string;
  contactPhone: string;
  description: string;
  contractValue: string;
  contractEnd: string;
}

interface VendorContextType {
  vendors: Vendor[];
  isLoading: boolean;
  addVendor: (vendor: Omit<Vendor, "id">) => void;
  updateVendor: (id: string, updates: Partial<Vendor>) => void;
  deleteVendor: (id: string) => void;
  getVendor: (id: string) => Vendor | undefined;
}

const VendorContext = createContext<VendorContextType | undefined>(undefined);

export function VendorProvider({ children }: { children: ReactNode }) {
  const { hasTenant } = useAuth() as { hasTenant: boolean };
  const queryClient = useQueryClient();

  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ["vendors"],
    queryFn: vendorService.list,
    enabled: hasTenant,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["vendors"] });

  const addMutation = useMutation({
    mutationFn: (data: Omit<Vendor, "id">) => vendorService.create(data),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Vendor> }) =>
      vendorService.update(id, data),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vendorService.delete(id),
    onSuccess: invalidate,
  });

  const addVendor = (vendor: Omit<Vendor, "id">) => addMutation.mutate(vendor);
  const updateVendor = (id: string, updates: Partial<Vendor>) =>
    updateMutation.mutate({ id, data: updates });
  const deleteVendor = (id: string) => deleteMutation.mutate(id);
  const getVendor = (id: string) => vendors.find((v) => v.id === id);

  return (
    <VendorContext.Provider value={{ vendors, isLoading, addVendor, updateVendor, deleteVendor, getVendor }}>
      {children}
    </VendorContext.Provider>
  );
}

export function useVendors() {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error("useVendors must be used within a VendorProvider");
  }
  return context;
}
